/**
 * Binary WebSocket protocol encode/decode for Warmancer space combat.
 * All messages use ArrayBuffer/DataView for minimal bandwidth.
 */
import { MSG_INPUT, MSG_SNAPSHOT, MSG_PLAYER_JOIN, MSG_PLAYER_LEAVE, MSG_WORLD_SETUP, MSG_ASTEROID_SYNC, MSG_SCENERY_SETUP, INPUT_MSG_SIZE, SNAPSHOT_HEADER_SIZE, ENTITY_SIZE, SCENERY_SETUP_HEADER, SCENERY_ZONE_ENTRY_SIZE, FLAG_ALIVE, FLAG_AFTERBURNER, FLAG_THRUSTING, FLAG_BRAKING, FLAG_STRAFING_LEFT, FLAG_STRAFING_RIGHT, FLAG_FIRING, } from './constants.js';
// --- Input Message (Client -> Server): 14 bytes ---
// [0]     u8   message type = 0x01
// [1-4]   u32  sequence number
// [5-8]   u32  client tick
// [9]     u8   input bitfield
// [10-13] f32  targetAngle (mouse aim direction, radians)
export function encodeInputMessage(frame) {
    const buf = new ArrayBuffer(INPUT_MSG_SIZE);
    const view = new DataView(buf);
    view.setUint8(0, MSG_INPUT);
    view.setUint32(1, frame.sequence, true);
    view.setUint32(5, frame.tick, true);
    view.setUint8(9, frame.bitfield);
    view.setFloat32(10, frame.targetAngle, true);
    return buf;
}
export function decodeInputMessage(buf) {
    if (buf.byteLength !== INPUT_MSG_SIZE) {
        throw new Error(`Input buffer size mismatch: got ${buf.byteLength} bytes, expected ${INPUT_MSG_SIZE}`);
    }
    const view = new DataView(buf);
    const type = view.getUint8(0);
    if (type !== MSG_INPUT) {
        throw new Error(`Expected input message type ${MSG_INPUT}, got ${type}`);
    }
    return {
        sequence: view.getUint32(1, true),
        tick: view.getUint32(5, true),
        bitfield: view.getUint8(9),
        targetAngle: view.getFloat32(10, true),
    };
}
// --- Snapshot Message (Server -> Client): 14 + 30*N bytes ---
// [0]     u8   message type = 0x02
// [1-4]   u32  server tick
// [5-8]   u32  last acked input seq for this client
// [9-12]  f32  moon angle (server-authoritative, radians)
// [13]    u8   entity count (N)
// Per entity (30 bytes):
//   [0-3]   u32  entity ID
//   [4-7]   f32  x
//   [8-11]  f32  y
//   [12-15] f32  vx
//   [16-19] f32  vy
//   [20-23] f32  rotation
//   [24]    u8   health (0-100)
//   [25-28] f32  battery (full precision for afterburner threshold)
//   [29]    u8   flags (bit0=alive, bit1=afterburner, bit2=thrusting, bit3=braking)
export function encodeSnapshot(serverTick, lastAckedInput, entities, moonAngle) {
    const size = SNAPSHOT_HEADER_SIZE + entities.length * ENTITY_SIZE;
    const buf = new ArrayBuffer(size);
    const view = new DataView(buf);
    view.setUint8(0, MSG_SNAPSHOT);
    view.setUint32(1, serverTick, true);
    view.setUint32(5, lastAckedInput, true);
    view.setFloat32(9, moonAngle, true);
    view.setUint8(13, entities.length);
    let offset = SNAPSHOT_HEADER_SIZE;
    for (const entity of entities) {
        view.setUint32(offset, entity.entityId, true);
        view.setFloat32(offset + 4, entity.x, true);
        view.setFloat32(offset + 8, entity.y, true);
        view.setFloat32(offset + 12, entity.vx, true);
        view.setFloat32(offset + 16, entity.vy, true);
        view.setFloat32(offset + 20, entity.rotation, true);
        view.setUint8(offset + 24, Math.round(entity.health));
        view.setFloat32(offset + 25, entity.battery, true);
        let flags = 0;
        if (entity.alive)
            flags |= FLAG_ALIVE;
        if (entity.afterburner)
            flags |= FLAG_AFTERBURNER;
        if (entity.thrusting)
            flags |= FLAG_THRUSTING;
        if (entity.braking)
            flags |= FLAG_BRAKING;
        if (entity.strafingLeft)
            flags |= FLAG_STRAFING_LEFT;
        if (entity.strafingRight)
            flags |= FLAG_STRAFING_RIGHT;
        if (entity.firing)
            flags |= FLAG_FIRING;
        view.setUint8(offset + 29, flags);
        offset += ENTITY_SIZE;
    }
    return buf;
}
export function decodeSnapshot(buf) {
    if (buf.byteLength < SNAPSHOT_HEADER_SIZE) {
        throw new Error(`Snapshot buffer too small: ${buf.byteLength} bytes, minimum ${SNAPSHOT_HEADER_SIZE}. ` +
            `Server may be running old protocol (header was 10 bytes, now ${SNAPSHOT_HEADER_SIZE}).`);
    }
    const view = new DataView(buf);
    const type = view.getUint8(0);
    if (type !== MSG_SNAPSHOT) {
        throw new Error(`Expected snapshot message type ${MSG_SNAPSHOT}, got ${type}`);
    }
    const serverTick = view.getUint32(1, true);
    const lastAckedInput = view.getUint32(5, true);
    const moonAngle = view.getFloat32(9, true);
    const entityCount = view.getUint8(13);
    const expectedSize = SNAPSHOT_HEADER_SIZE + entityCount * ENTITY_SIZE;
    if (buf.byteLength !== expectedSize) {
        throw new Error(`Snapshot buffer size mismatch: got ${buf.byteLength} bytes, expected ${expectedSize} ` +
            `(header=${SNAPSHOT_HEADER_SIZE}, entities=${entityCount}, entitySize=${ENTITY_SIZE}). ` +
            `Protocol version mismatch — restart the server (wrangler dev).`);
    }
    const entities = [];
    let offset = SNAPSHOT_HEADER_SIZE;
    for (let i = 0; i < entityCount; i++) {
        const flags = view.getUint8(offset + 29);
        entities.push({
            entityId: view.getUint32(offset, true),
            x: view.getFloat32(offset + 4, true),
            y: view.getFloat32(offset + 8, true),
            vx: view.getFloat32(offset + 12, true),
            vy: view.getFloat32(offset + 16, true),
            rotation: view.getFloat32(offset + 20, true),
            health: view.getUint8(offset + 24),
            battery: view.getFloat32(offset + 25, true),
            alive: (flags & FLAG_ALIVE) !== 0,
            afterburner: (flags & FLAG_AFTERBURNER) !== 0,
            thrusting: (flags & FLAG_THRUSTING) !== 0,
            braking: (flags & FLAG_BRAKING) !== 0,
            strafingLeft: (flags & FLAG_STRAFING_LEFT) !== 0,
            strafingRight: (flags & FLAG_STRAFING_RIGHT) !== 0,
            firing: (flags & FLAG_FIRING) !== 0,
        });
        offset += ENTITY_SIZE;
    }
    return { serverTick, lastAckedInput, moonAngle, entities };
}
// --- WorldSetup Message (Server -> Client) ---
// [0]     u8   message type = 0x05
// [1-4]   u32  localEntityId
// [5-8]   f32  bounds half-size
// [9-12]  f32  planet x
// [13-16] f32  planet y
// [17-20] f32  planet radius
// [21-24] f32  planet mass
// [25-28] f32  moon radius
// [29-32] f32  moon mass
// [33-36] f32  moon orbit radius
// [37-40] f32  moon orbit speed
// [41-44] u32  server tick (so client can sync moon angle)
// [45]    u8   asteroid count
// Per asteroid (20 bytes):
//   [0-3]   f32  x
//   [4-7]   f32  y
//   [8-11]  f32  radius
//   [12-15] f32  vx
//   [16-19] f32  vy
const WORLD_SETUP_HEADER = 46;
const ASTEROID_ENTRY_SIZE = 20;
export function encodeWorldSetup(setup) {
    const size = WORLD_SETUP_HEADER + setup.asteroids.length * ASTEROID_ENTRY_SIZE;
    const buf = new ArrayBuffer(size);
    const view = new DataView(buf);
    view.setUint8(0, MSG_WORLD_SETUP);
    view.setUint32(1, setup.localEntityId, true);
    view.setFloat32(5, setup.boundsHalfSize, true);
    view.setFloat32(9, setup.planet.x, true);
    view.setFloat32(13, setup.planet.y, true);
    view.setFloat32(17, setup.planet.radius, true);
    view.setFloat32(21, setup.planet.mass, true);
    view.setFloat32(25, setup.moon.radius, true);
    view.setFloat32(29, setup.moon.mass, true);
    view.setFloat32(33, setup.moon.orbitRadius, true);
    view.setFloat32(37, setup.moon.orbitSpeed, true);
    view.setUint32(41, setup.serverTick, true);
    view.setUint8(45, setup.asteroids.length);
    let offset = WORLD_SETUP_HEADER;
    for (const asteroid of setup.asteroids) {
        view.setFloat32(offset, asteroid.x, true);
        view.setFloat32(offset + 4, asteroid.y, true);
        view.setFloat32(offset + 8, asteroid.radius, true);
        view.setFloat32(offset + 12, asteroid.vx, true);
        view.setFloat32(offset + 16, asteroid.vy, true);
        offset += ASTEROID_ENTRY_SIZE;
    }
    return buf;
}
export function decodeWorldSetup(buf) {
    const view = new DataView(buf);
    const type = view.getUint8(0);
    if (type !== MSG_WORLD_SETUP) {
        throw new Error(`Expected world setup message type ${MSG_WORLD_SETUP}, got ${type}`);
    }
    const serverTick = view.getUint32(41, true);
    const asteroidCount = view.getUint8(45);
    const asteroids = [];
    let offset = WORLD_SETUP_HEADER;
    for (let i = 0; i < asteroidCount; i++) {
        asteroids.push({
            x: view.getFloat32(offset, true),
            y: view.getFloat32(offset + 4, true),
            radius: view.getFloat32(offset + 8, true),
            vx: view.getFloat32(offset + 12, true),
            vy: view.getFloat32(offset + 16, true),
        });
        offset += ASTEROID_ENTRY_SIZE;
    }
    return {
        localEntityId: view.getUint32(1, true),
        boundsHalfSize: view.getFloat32(5, true),
        planet: {
            x: view.getFloat32(9, true),
            y: view.getFloat32(13, true),
            radius: view.getFloat32(17, true),
            mass: view.getFloat32(21, true),
        },
        moon: {
            radius: view.getFloat32(25, true),
            mass: view.getFloat32(29, true),
            orbitRadius: view.getFloat32(33, true),
            orbitSpeed: view.getFloat32(37, true),
        },
        serverTick,
        asteroids,
    };
}
// --- AsteroidSync Message (Server -> Client) ---
// Periodic sync of all asteroid positions + velocities.
// Sent at 20Hz (every 3 ticks) to keep asteroid positions accurate for client collision.
// [0]     u8   message type = 0x06
// [1]     u8   asteroid count
// Per asteroid (16 bytes):
//   [0-3]   f32  x
//   [4-7]   f32  y
//   [8-11]  f32  vx
//   [12-15] f32  vy
const ASTEROID_SYNC_HEADER = 2;
const ASTEROID_SYNC_ENTRY = 16;
export function encodeAsteroidSync(asteroids) {
    const size = ASTEROID_SYNC_HEADER + asteroids.length * ASTEROID_SYNC_ENTRY;
    const buf = new ArrayBuffer(size);
    const view = new DataView(buf);
    view.setUint8(0, MSG_ASTEROID_SYNC);
    view.setUint8(1, asteroids.length);
    let offset = ASTEROID_SYNC_HEADER;
    for (const a of asteroids) {
        view.setFloat32(offset, a.x, true);
        view.setFloat32(offset + 4, a.y, true);
        view.setFloat32(offset + 8, a.vx, true);
        view.setFloat32(offset + 12, a.vy, true);
        offset += ASTEROID_SYNC_ENTRY;
    }
    return buf;
}
export function decodeAsteroidSync(buf) {
    const view = new DataView(buf);
    const type = view.getUint8(0);
    if (type !== MSG_ASTEROID_SYNC) {
        throw new Error(`Expected asteroid sync message type ${MSG_ASTEROID_SYNC}, got ${type}`);
    }
    const count = view.getUint8(1);
    const result = [];
    let offset = ASTEROID_SYNC_HEADER;
    for (let i = 0; i < count; i++) {
        result.push({
            x: view.getFloat32(offset, true),
            y: view.getFloat32(offset + 4, true),
            vx: view.getFloat32(offset + 8, true),
            vy: view.getFloat32(offset + 12, true),
        });
        offset += ASTEROID_SYNC_ENTRY;
    }
    return result;
}
// --- PlayerJoin Message (Server -> Client) ---
// [0]     u8   type = 0x03
// [1-4]   u32  entity ID
// [5]     u8   username length
// [6+]    utf8 username
export function encodePlayerJoin(msg) {
    const encoder = new TextEncoder();
    const nameBytes = encoder.encode(msg.username);
    const buf = new ArrayBuffer(6 + nameBytes.length);
    const view = new DataView(buf);
    const bytes = new Uint8Array(buf);
    view.setUint8(0, MSG_PLAYER_JOIN);
    view.setUint32(1, msg.entityId, true);
    view.setUint8(5, nameBytes.length);
    bytes.set(nameBytes, 6);
    return buf;
}
export function decodePlayerJoin(buf) {
    const view = new DataView(buf);
    const type = view.getUint8(0);
    if (type !== MSG_PLAYER_JOIN) {
        throw new Error(`Expected player join message type ${MSG_PLAYER_JOIN}, got ${type}`);
    }
    const entityId = view.getUint32(1, true);
    const nameLen = view.getUint8(5);
    const decoder = new TextDecoder();
    const username = decoder.decode(new Uint8Array(buf, 6, nameLen));
    return { entityId, username };
}
// --- PlayerLeave Message (Server -> Client) ---
// [0]     u8   type = 0x04
// [1-4]   u32  entity ID
// [5]     u8   username length
// [6+]    utf8 username
export function encodePlayerLeave(msg) {
    const encoder = new TextEncoder();
    const nameBytes = encoder.encode(msg.username);
    const buf = new ArrayBuffer(6 + nameBytes.length);
    const view = new DataView(buf);
    const bytes = new Uint8Array(buf);
    view.setUint8(0, MSG_PLAYER_LEAVE);
    view.setUint32(1, msg.entityId, true);
    view.setUint8(5, nameBytes.length);
    bytes.set(nameBytes, 6);
    return buf;
}
export function decodePlayerLeave(buf) {
    const view = new DataView(buf);
    const type = view.getUint8(0);
    if (type !== MSG_PLAYER_LEAVE) {
        throw new Error(`Expected player leave message type ${MSG_PLAYER_LEAVE}, got ${type}`);
    }
    const entityId = view.getUint32(1, true);
    const nameLen = view.getUint8(5);
    const decoder = new TextDecoder();
    const username = decoder.decode(new Uint8Array(buf, 6, nameLen));
    return { entityId, username };
}
// --- ScenerySetup Message (Server -> Client) ---
// [0]     u8   message type = 0x07
// [1-4]   u32  masterSeed
// [5]     u8   zone count
// Per zone (25 bytes):
//   [0]     u8   zone type
//   [1-4]   f32  x
//   [5-8]   f32  y
//   [9-12]  f32  radius
//   [13-16] f32  param1
//   [17-20] f32  param2
//   [21-24] u32  seed
export function encodeScenerySetup(setup) {
    const size = SCENERY_SETUP_HEADER + setup.zones.length * SCENERY_ZONE_ENTRY_SIZE;
    const buf = new ArrayBuffer(size);
    const view = new DataView(buf);
    view.setUint8(0, MSG_SCENERY_SETUP);
    view.setUint32(1, setup.masterSeed, true);
    view.setUint8(5, setup.zones.length);
    let offset = SCENERY_SETUP_HEADER;
    for (const zone of setup.zones) {
        view.setUint8(offset, zone.type);
        view.setFloat32(offset + 1, zone.x, true);
        view.setFloat32(offset + 5, zone.y, true);
        view.setFloat32(offset + 9, zone.radius, true);
        view.setFloat32(offset + 13, zone.param1, true);
        view.setFloat32(offset + 17, zone.param2, true);
        view.setUint32(offset + 21, zone.seed, true);
        offset += SCENERY_ZONE_ENTRY_SIZE;
    }
    return buf;
}
export function decodeScenerySetup(buf) {
    if (buf.byteLength < SCENERY_SETUP_HEADER) {
        throw new Error(`ScenerySetup buffer too small: ${buf.byteLength} bytes, minimum ${SCENERY_SETUP_HEADER}`);
    }
    const view = new DataView(buf);
    const type = view.getUint8(0);
    if (type !== MSG_SCENERY_SETUP) {
        throw new Error(`Expected scenery setup message type ${MSG_SCENERY_SETUP}, got ${type}`);
    }
    const masterSeed = view.getUint32(1, true);
    const zoneCount = view.getUint8(5);
    const expectedSize = SCENERY_SETUP_HEADER + zoneCount * SCENERY_ZONE_ENTRY_SIZE;
    if (buf.byteLength !== expectedSize) {
        throw new Error(`ScenerySetup buffer size mismatch: got ${buf.byteLength} bytes, expected ${expectedSize} ` +
            `(header=${SCENERY_SETUP_HEADER}, zones=${zoneCount}, zoneSize=${SCENERY_ZONE_ENTRY_SIZE})`);
    }
    const zones = [];
    let offset = SCENERY_SETUP_HEADER;
    for (let i = 0; i < zoneCount; i++) {
        zones.push({
            type: view.getUint8(offset),
            x: view.getFloat32(offset + 1, true),
            y: view.getFloat32(offset + 5, true),
            radius: view.getFloat32(offset + 9, true),
            param1: view.getFloat32(offset + 13, true),
            param2: view.getFloat32(offset + 17, true),
            seed: view.getUint32(offset + 21, true),
        });
        offset += SCENERY_ZONE_ENTRY_SIZE;
    }
    return { masterSeed, zones };
}
/**
 * Get message type from a binary message buffer.
 */
export function getMessageType(buf) {
    if (buf.byteLength === 0) {
        throw new Error('Empty message buffer');
    }
    return new DataView(buf).getUint8(0);
}
//# sourceMappingURL=protocol.js.map